import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Code, ConnectError } from '@connectrpc/connect';
import { SpecraBadge, SpecraButton, SpecraCard, SpecraCardContent, SpecraCheckboxField, SpecraHeading, SpecraOverlayDialog, SpecraText } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { useAuth } from '../../contexts/authContext';
import { projectDataClient } from '../../lib/connect/clients';
import { ProposalContextContents } from './ProposalContextReconciliation';
const ids = Testids.proposalContextAdoption;
const sectionLabels = {
    staffing: 'Early staffing / LOE',
    solution: 'Solution',
    win_themes: 'Win themes',
    management_approach: 'Management approach'
};
const kindLabels = {
    staffing: 'Early staffing / LOE',
    solution: 'Solution',
    win_theme: 'Win theme',
    output: 'Output',
    capture_solution_revisions: 'Solution',
    manual_material: 'Management material'
};
function unavailable(error) {
    return error instanceof ConnectError && [
        Code.PermissionDenied,
        Code.NotFound,
        Code.Unauthenticated
    ].includes(error.code);
}
function candidateKey(candidate) {
    return JSON.stringify([
        candidate.captureProjectId,
        candidate.sourceKind,
        candidate.sourceId,
        candidate.sourceVersionId,
        candidate.handoffId
    ]);
}
function allowedSections(candidate) {
    const supported = candidate.sourceKind === 'output' ? [
        'solution',
        'win_themes',
        'management_approach'
    ] : candidate.sourceKind === 'solution' ? [
        'solution'
    ] : candidate.sourceKind === 'staffing' ? [
        'staffing'
    ] : candidate.sourceKind === 'win_theme' ? [
        'win_themes'
    ] : [];
    if (!candidate.captureProjectId || !candidate.sourceId || !candidate.sourceVersionId || candidate.sourceKind === 'output' && !candidate.handoffId || candidate.sourceKind === 'staffing' && candidate.sourceId !== candidate.captureProjectId) return [];
    return candidate.sectionKeys.filter((key)=>supported.includes(key) && candidate.content?.sections.some((section)=>section.key === key));
}
function SafeContent({ content }) {
    if (content?.staffing?.length) return /*#__PURE__*/ _jsx(ProposalContextContents, {
        content: content
    });
    return content?.sections.map((section)=>/*#__PURE__*/ _jsxs("section", {
            "data-testid": ids.content,
            "data-section-key": section.key,
            className: "space-y-2",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    size: "sm",
                    children: sectionLabels[section.key] ?? 'Context'
                }),
                section.wording && /*#__PURE__*/ _jsx(SpecraText, {
                    className: "whitespace-pre-wrap",
                    children: section.wording
                }),
                section.sources.map((source)=>/*#__PURE__*/ _jsxs("section", {
                        className: "space-y-2",
                        children: [
                            /*#__PURE__*/ _jsxs(SpecraText, {
                                size: "meta",
                                children: [
                                    kindLabels[source.kind] ?? 'Source',
                                    " · Source version: ",
                                    source.versionId
                                ]
                            }),
                            source.approach && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        tone: "muted",
                                        children: "Approach"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        className: "whitespace-pre-wrap",
                                        children: source.approach
                                    })
                                ]
                            }),
                            source.intendedOutcomes && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        tone: "muted",
                                        children: "Intended outcomes"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        className: "whitespace-pre-wrap",
                                        children: source.intendedOutcomes
                                    })
                                ]
                            }),
                            source.rationale && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        tone: "muted",
                                        children: "Rationale"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        className: "whitespace-pre-wrap",
                                        children: source.rationale
                                    })
                                ]
                            }),
                            source.narrative && /*#__PURE__*/ _jsx(SpecraText, {
                                className: "whitespace-pre-wrap",
                                children: source.narrative
                            }),
                            source.limitations && /*#__PURE__*/ _jsxs(_Fragment, {
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        tone: "muted",
                                        children: "Limitations"
                                    }),
                                    /*#__PURE__*/ _jsx(SpecraText, {
                                        className: "whitespace-pre-wrap",
                                        children: source.limitations
                                    })
                                ]
                            })
                        ]
                    }, `${source.kind}:${source.id}:${source.versionId}`))
            ]
        }, section.key));
}
export function ProposalContextAdoption({ projectId }) {
    const { user } = useAuth();
    if (!user) return null;
    return /*#__PURE__*/ _jsx(AdoptionPanel, {
        projectId: projectId,
        account: [
            user.tenant_id ?? '',
            user.id
        ]
    }, JSON.stringify([
        user.tenant_id,
        user.id,
        projectId
    ]));
}
function AdoptionPanel({ projectId, account }) {
    const [open, setOpen] = useState(false);
    const [selectedKey, setSelectedKey] = useState('');
    const [mode, setMode] = useState('');
    const [sections, setSections] = useState([]);
    const [message, setMessage] = useState('');
    const [pending, setPending] = useState(false);
    const submitting = useRef(false);
    const query = useQuery({
        queryKey: [
            'proposal-context-adoption',
            ...account,
            projectId
        ],
        queryFn: async ({ signal })=>{
            let adoptions;
            try {
                adoptions = (await projectDataClient.listProposalContexts({
                    proposalProjectId: projectId
                }, {
                    signal
                })).adoptions;
            } catch (error) {
                if (unavailable(error)) return {
                    adoptions: [],
                    candidates: [],
                    writable: false
                };
                throw error;
            }
            let writable = false;
            try {
                await projectDataClient.checkProposalContextAccess({
                    proposalProjectId: projectId,
                    write: true
                }, {
                    signal
                });
                writable = true;
            } catch (error) {
                if (!unavailable(error)) throw error;
            }
            const candidates = writable ? (await projectDataClient.listProposalContextCandidates({
                proposalProjectId: projectId
            }, {
                signal
            })).candidates : [];
            return {
                adoptions,
                writable,
                candidates: candidates.filter((candidate)=>candidate.proposalProjectId === projectId && allowedSections(candidate).length > 0)
            };
        },
        retry: false,
        staleTime: 0,
        gcTime: 0,
        refetchInterval: 15000,
        refetchOnWindowFocus: 'always'
    });
    // Never render cached source content during a permission refresh or after an error.
    const data = !query.isError && !query.isFetching ? query.data : undefined;
    const selected = data?.candidates.find((candidate)=>candidateKey(candidate) === selectedKey);
    const sectionKeys = selected ? sections.filter((key)=>allowedSections(selected).includes(key)) : [];
    const valid = !!data?.writable && !!selected && !!mode && sectionKeys.length > 0;
    function clearSelection() {
        setSelectedKey('');
        setMode('');
        setSections([]);
        setMessage('');
    }
    function close() {
        if (!submitting.current) {
            setOpen(false);
            clearSelection();
        }
    }
    async function adopt() {
        if (!valid || !selected || !mode || submitting.current) return;
        submitting.current = true;
        setPending(true);
        setMessage('');
        try {
            await projectDataClient.adoptProposalContext({
                proposalProjectId: projectId,
                captureProjectId: selected.captureProjectId,
                sourceKind: selected.sourceKind,
                sourceId: selected.sourceId,
                sourceVersionId: selected.sourceVersionId,
                handoffId: selected.handoffId,
                mode,
                sectionKeys
            });
            clearSelection();
            setOpen(false);
            setMessage('Context added.');
        } catch  {
            setMessage('Context could not be added. Please retry.');
        } finally{
            await query.refetch();
            submitting.current = false;
            setPending(false);
        }
    }
    const loading = /*#__PURE__*/ _jsx("output", {
        children: /*#__PURE__*/ _jsx(SpecraText, {
            children: "Loading context… A slow connection may take a moment."
        })
    });
    const error = /*#__PURE__*/ _jsxs("section", {
        children: [
            /*#__PURE__*/ _jsx(SpecraText, {
                role: "alert",
                children: "Context could not be loaded. Please retry."
            }),
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>void query.refetch(),
                children: "Retry"
            })
        ]
    });
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": ids.root,
        className: "max-h-64 shrink-0 overflow-y-auto",
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx(SpecraHeading, {
                    size: "sm",
                    children: "Pursuit context"
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": ids.refresh,
                    variant: "outline",
                    disabled: query.isFetching || pending,
                    onClick: ()=>void query.refetch(),
                    children: "Refresh context"
                }),
                query.isError ? error : query.isFetching ? loading : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        data?.writable && /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": ids.open,
                            onClick: ()=>{
                                clearSelection();
                                setOpen(true);
                            },
                            children: "Bring in pursuit context"
                        }),
                        !data?.adoptions.length && /*#__PURE__*/ _jsx(SpecraText, {
                            children: "No context brought in yet."
                        }),
                        data?.adoptions.map((adoption)=>/*#__PURE__*/ _jsxs("section", {
                                "data-testid": ids.adoption,
                                "data-adoption-id": adoption.id,
                                "data-mode": adoption.mode,
                                className: "space-y-2",
                                children: [
                                    /*#__PURE__*/ _jsx(SpecraHeading, {
                                        size: "sm",
                                        children: kindLabels[adoption.sourceKind] ?? 'Context'
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        children: [
                                            "Transfer mode:",
                                            ' ',
                                            adoption.mode === 'live' ? 'Keep in sync with the source' : 'Copy as it is now'
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        children: [
                                            "Original version: ",
                                            adoption.originalVersionId
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs(SpecraText, {
                                        children: [
                                            "Current version: ",
                                            adoption.currentVersionId
                                        ]
                                    }),
                                    adoption.mode === 'live' && adoption.changed && /*#__PURE__*/ _jsx(SpecraBadge, {
                                        "data-testid": ids.changed,
                                        variant: "warning",
                                        children: "Source changed"
                                    }),
                                    /*#__PURE__*/ _jsx(SafeContent, {
                                        content: adoption.content
                                    })
                                ]
                            }, adoption.id))
                    ]
                }),
                message && !open && /*#__PURE__*/ _jsx("output", {
                    "data-testid": ids.message,
                    children: /*#__PURE__*/ _jsx(SpecraText, {
                        children: message
                    })
                }),
                /*#__PURE__*/ _jsxs(SpecraOverlayDialog, {
                    open: open,
                    onOpenChange: (next)=>{
                        if (!next) close();
                    },
                    title: "Bring in pursuit context",
                    testId: ids.dialog,
                    size: "lg",
                    footer: /*#__PURE__*/ _jsxs("section", {
                        className: "flex flex-wrap gap-2 p-4",
                        children: [
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.cancel,
                                variant: "outline",
                                disabled: pending,
                                onClick: close,
                                children: "Cancel"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.back,
                                variant: "outline",
                                disabled: pending || !selectedKey,
                                onClick: clearSelection,
                                children: "Back"
                            }),
                            /*#__PURE__*/ _jsx(SpecraButton, {
                                "data-testid": ids.submit,
                                disabled: !valid || pending,
                                isLoading: pending,
                                onClick: ()=>void adopt(),
                                children: "Bring in selected context"
                            })
                        ]
                    }),
                    children: [
                        /*#__PURE__*/ _jsx(SpecraText, {
                            children: 'Choose one item, its sections and a transfer mode. "Copy as it is now" keeps the wording as it is. "Keep in sync with the source" follows the source and needs continued access. Neither changes the proposal draft automatically.'
                        }),
                        /*#__PURE__*/ _jsx(SpecraHeading, {
                            size: "sm",
                            children: "Transfer mode"
                        }),
                        query.isError ? error : query.isFetching ? loading : !data?.writable || !data.candidates.length ? /*#__PURE__*/ _jsx(SpecraText, {
                            children: "No context available."
                        }) : /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-3",
                            children: [
                                data.candidates.map((candidate)=>/*#__PURE__*/ _jsxs("section", {
                                        "data-testid": ids.candidate,
                                        "data-source-kind": candidate.sourceKind,
                                        "data-source-id": candidate.sourceId,
                                        "data-source-version-id": candidate.sourceVersionId,
                                        className: "space-y-2",
                                        children: [
                                            /*#__PURE__*/ _jsxs(SpecraButton, {
                                                "data-testid": ids.select,
                                                variant: "outline",
                                                "aria-pressed": selectedKey === candidateKey(candidate),
                                                disabled: pending,
                                                onClick: ()=>{
                                                    clearSelection();
                                                    setSelectedKey(candidateKey(candidate));
                                                },
                                                children: [
                                                    "Select ",
                                                    kindLabels[candidate.sourceKind],
                                                    " · ",
                                                    candidate.sourceVersionId
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsx(SafeContent, {
                                                content: candidate.content
                                            })
                                        ]
                                    }, candidateKey(candidate))),
                                selected && /*#__PURE__*/ _jsxs("section", {
                                    className: "space-y-3",
                                    children: [
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": ids.mode,
                                            "data-mode": "snapshot",
                                            "aria-pressed": mode === 'snapshot',
                                            disabled: pending,
                                            onClick: ()=>setMode('snapshot'),
                                            children: "Copy as it is now"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraButton, {
                                            "data-testid": ids.mode,
                                            "data-mode": "live",
                                            "aria-pressed": mode === 'live',
                                            disabled: pending,
                                            onClick: ()=>setMode('live'),
                                            children: "Keep in sync with the source"
                                        }),
                                        /*#__PURE__*/ _jsx(SpecraHeading, {
                                            size: "sm",
                                            children: "Sections to bring in"
                                        }),
                                        allowedSections(selected).map((key)=>/*#__PURE__*/ _jsx(SpecraCheckboxField, {
                                                "data-testid": ids.section,
                                                "data-section-key": key,
                                                label: sectionLabels[key],
                                                controlProps: {
                                                    'aria-label': sectionLabels[key],
                                                    checked: sections.includes(key),
                                                    disabled: pending,
                                                    onCheckedChange: (checked)=>setSections((current)=>checked === true ? [
                                                                ...current,
                                                                key
                                                            ] : current.filter((value)=>value !== key))
                                                }
                                            }, key))
                                    ]
                                })
                            ]
                        }),
                        pending && /*#__PURE__*/ _jsx("output", {
                            children: /*#__PURE__*/ _jsx(SpecraText, {
                                children: "Adding context… Please wait."
                            })
                        }),
                        message && /*#__PURE__*/ _jsx("output", {
                            "data-testid": ids.message,
                            children: /*#__PURE__*/ _jsx(SpecraText, {
                                children: message
                            })
                        })
                    ]
                })
            ]
        })
    });
}
